import {
  Button,
  Callout,
  Card,
  FormLayout,
  GridFieldGroup,
  Heading,
  Link,
  LoadingDots,
  MaskedInput,
  Paragraph,
  Select,
  SubmitButton,
  TextInput,
  useDialog,
} from '@expressable/syntax-ds';
import { SealQuestion } from '@phosphor-icons/react';
import {
  DirectSignupAddSecondaryInsuranceDialog,
  DirectSignupChangeInsuranceDialog,
  DirectSignupEditAppointmentDialog,
} from 'components/Dialog';
import { SubscriberRelationshipEnum } from 'domains/client';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { handleCheckEligibility } from 'domains/signupDirect/Evaluation/Insurance/eligibility';
import { getSubscriberRelationshipOptions } from 'domains/signupDirect/misc/constants';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { paymentMethodIsInsurance, EvaluationFormType } from 'domains/signupDirect/schemas/evaluationSchema';
import { InsuranceEligibilityCheckCard } from 'domains/signupDirect/SyntaxComps/EligibilityCard';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTrack } from 'utils/hooks/useMixPanel';
import { createLeadOnEligibilityCheck } from './createLeadOnEligibilityCheck';
import config from 'config';
import { EligibilityCheckResult } from 'domains/signupv4/Insurance/types';
import { getTempLeadId } from 'domains/signupDirect/misc/leadStorage';

export const DirectSignupInsuranceEligibilitySection = ({
  showUpdateInfo,
  setShowUpdateInfo,
}: {
  showUpdateInfo?: boolean;
  setShowUpdateInfo?: (show: boolean) => void;
}) => {
  const { track } = useTrack();
  const { dialogStateProps: addSecondaryInsuranceProps, onOpen: onOpenAddSecondaryInsurance } = useDialog();
  const { dialogStateProps: changeInsuranceProps, onOpen: onOpenChangeInsurance } = useDialog();
  const { dialogStateProps: editAppointmentProps } = useDialog();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormContext<EvaluationFormType>();
  const {
    filters,
    evaluationSchedule,
    onReturnToFilters,
    onContinueToConsult,
    onContinueToScheduleEval,
    onSaveIncompleteEvalForm,
    resumedLeadState,
    isResumedLead,
  } = useSignupDirectCtx();
  if (!filters) throw 'Expected filters to be defined';
  if (!evaluationSchedule) throw 'Expected evaluationSchedule to be defined';

  const shouldSkipEligibility = resumedLeadState?.shouldSkipEligibility;

  const paymentMethod = useWatch<EvaluationFormType, 'billingInfo'>({ name: 'billingInfo' });
  if (!paymentMethodIsInsurance(paymentMethod)) throw 'Expected paymentMethod.type to be insurance';

  const { insuranceData } = paymentMethod;

  if (!insuranceData) throw 'Expected currentInsuranceData to be defined';

  const onSubmitEligibilityCheck = handleSubmit(async data => {
    const checkResult = await handleCheckEligibility({ data, setValue });
    await createLeadOnEligibilityCheck(data, getValues, setValue, filters, checkResult);
  });

  const checkResult = useWatch<EvaluationFormType, 'billingInfo.eligibilityCheckResult'>({
    name: 'billingInfo.eligibilityCheckResult',
  });

  const subscriberRelationshipOptions = getSubscriberRelationshipOptions(t);

  const JOTFORM_INSURANCE_CARD = 'https://forms.expressable.com/250986857903978';

  const handleSubmitInsuranceCard = () => {
    const leadId = getValues('leadId') ?? getTempLeadId();
    const insId = getValues('insuranceLeadId');

    const params = new URLSearchParams({
      leadID: leadId as string,
      insID: insId as string,
    });

    window.location.replace(`${JOTFORM_INSURANCE_CARD}?${params.toString()}`);
  };

  return (
    <>
      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.insurance.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.insurance.subtitle')}
          </Paragraph>
        </HeadingCombo>

        <div className="flex flex-col gap-e16">
            <TextInput
              data-testid="insurance-plan-readonly-input"
              label={t('directSignup.sections.insurance.labels.insurancePlan')}
              {...register('billingInfo.insuranceData.displayName')}
              disabled
            />

            {!shouldSkipEligibility && (
              <Button data-testid="change-insurance-button" variant="link" size="sm" onClick={onOpenChangeInsurance}>
                {t('directSignup.sections.insurance.changeInsurance')}
              </Button>
            )}
        </div>

        <GridFieldGroup>
          <TextInput
            data-testid="insurance-member-id-input"
            label={t('directSignup.sections.insurance.labels.memberId')}
            disabled={resumedLeadState?.shouldSkipEligibility}
            hint={t('directSignup.sections.insurance.hint.memberId')}
            {...register('billingInfo.memberId')}
          />

          <TextInput
            data-testid="insurance-group-id-input"
            label={t('directSignup.sections.insurance.labels.groupId')}
            disabled={resumedLeadState?.shouldSkipEligibility}
            hint={t('directSignup.sections.insurance.hint.groupId')}
            {...register('billingInfo.groupId')}
          />
        </GridFieldGroup>

        {paymentMethod.type === 'insurance-commercial' && (
          <Callout className="w-full" type="info">
            {t('directSignup.sections.insurance.labels.subscriber.verifySubscriberInfoCopy')}
          </Callout>
        )}

        {paymentMethod.type === 'insurance-commercial' && (
          <GridFieldGroup>
            <Select
              label={t('directSignup.sections.insurance.labels.subscriber.relationshipToClient')}
              name="billingInfo.subscriber.relationShipToClient"
              options={subscriberRelationshipOptions}
              disabled={resumedLeadState?.shouldSkipEligibility}
            />

            {paymentMethod.subscriber?.relationShipToClient !== SubscriberRelationshipEnum.Self && (
              <>
                <MaskedInput
                  data-testid="insurance-subscriber-dob-input"
                  label={t('directSignup.sections.insurance.labels.subscriber.dateOfBirth')}
                  {...register('billingInfo.subscriber.dateOfBirth')}
                  mask="MM/DD/YYYY"
                  showMask
                  type="tel"
                  replacement={{
                    M: /[0-1]|[0-9]/,
                    D: /[0-3]|[0-9]/,
                    Y: /\d/,
                  }}
                  disabled={resumedLeadState?.shouldSkipEligibility}
                />
                <TextInput
                  label={t('directSignup.sections.insurance.labels.subscriber.firstName')}
                  {...register('billingInfo.subscriber.firstName')}
                  disabled={resumedLeadState?.shouldSkipEligibility}
                />
                <TextInput
                  label={t('directSignup.sections.insurance.labels.subscriber.lastName')}
                  {...register('billingInfo.subscriber.lastName')}
                  disabled={resumedLeadState?.shouldSkipEligibility}
                />
              </>
            )}
          </GridFieldGroup>
        )}

        {!shouldSkipEligibility && (
          <Button
            data-testid="add-secondary-insurance-button"
            variant="link"
            className="mb-64"
            onClick={onOpenAddSecondaryInsurance}
          >
            {t('directSignup.sections.insurance.addSecondaryInsurance')}
          </Button>
        )}

        <Heading noDefaultMargins level="h5">
          {t('directSignup.sections.billing')}
        </Heading>

        {isSubmitting &&
          checkResult !== EligibilityCheckResult.IN_NETWORK &&
          checkResult !== EligibilityCheckResult.OUT_OF_NETWORK ? (
          <Card variant="secondary" className={`border p-e32 min-h-[313px] flex items-center`}>
            <LoadingDots
              className="bg-transparent static"
              message={t('directSignup.sections.insurance.eligibilityCheck.loading')}
            />
          </Card>
        ) : (
          <>
            {!checkResult && insuranceData.networkStatus === 'In-network' && (
              <Card variant="secondary" className={`border p-e32`}>
                <Heading level="h6" className="flex items-center -ml-8" noDefaultMargins>
                  <div className="p-e12 rounded-full bg-gray-100 text-gray-600 mx-8">
                    <SealQuestion weight="fill" size={20} />
                  </div>
                  {t('directSignup.sections.insurance.eligibilityCheck.title')}
                </Heading>
                <SubmitButton
                  data-testid="check-insurance-eligibility-button"
                  variant="secondary"
                  className="my-32"
                  onClick={onSubmitEligibilityCheck}
                >
                  {t('directSignup.sections.insurance.eligibilityCheck.submit')}
                </SubmitButton>

                <Paragraph size="sm" variant="secondary" className="mt-e16" noDefaultMargins>
                  {t('directSignup.eval.form.terms.1')}
                  <Link to={config.get('termsUrl') as string} external>
                    {t('directSignup.eval.form.terms.2')}
                  </Link>
                  {t('directSignup.eval.form.terms.7')}
                  <Link to={config.get('privacyPolicyUrl') as string} external>
                    {t('directSignup.eval.form.terms.8')}
                  </Link>
                  {t('directSignup.eval.form.terms.9')}
                  {t('directSignup.eval.form.terms.10')}
                </Paragraph>
              </Card>
            )}

            <div className="flex flex-col gap-e64">
              {(checkResult || insuranceData.networkStatus === 'Out-of-network') && (
                <InsuranceEligibilityCheckCard
                  showUpdateInfo={showUpdateInfo}
                  setShowUpdateInfo={setShowUpdateInfo}
                  result={checkResult}
                  onSubmitEligibilityCheck={onSubmitEligibilityCheck}
                  isSkippedEligibility={shouldSkipEligibility}
                  onSubmitInsuranceCard={handleSubmitInsuranceCard}
                />
              )}
            </div>
          </>
        )}
      </FormLayout.Fields>

      <DirectSignupAddSecondaryInsuranceDialog
        {...addSecondaryInsuranceProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          onSaveIncompleteEvalForm(incompleteForm);
          onContinueToConsult();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.ADD_SECONDARY_INSURANCE, { isResumedLead });
        }}
      />
      <DirectSignupChangeInsuranceDialog
        {...changeInsuranceProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          onSaveIncompleteEvalForm(incompleteForm);
          onReturnToFilters();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CHANGE_PAYMENT_METHOD.FROM_INSURANCE, { isResumedLead });
        }}
      />
      <DirectSignupEditAppointmentDialog
        {...editAppointmentProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          onSaveIncompleteEvalForm(incompleteForm);
          onContinueToScheduleEval();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CHANGE_APPOINTMENT, { isResumedLead });
        }}
      />
    </>
  );
};
